import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  notification,
  Radio,
  Select,
  Alert,
  Card,
  Typography,
  Space,
  Tag,
  Tooltip,
  Input,
  Row,
  Col,
  Spin,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import BackgroundTop from '../BackgroundTop/BackgroundTop';
import { t } from '../../i18n/i18n';
import {
  getCustomEmailsAll,
  getLastEmail,
  getTemplateByDefault,
  saveTemplateEmail,
} from '../../API/services/alkymersService';
import { fetchUniqueWorkspaceMembers } from '../../utils/workspaceMembersUtils';
import { setSubjectEmail, setTemplate } from '../../redux/actions/screeningActions';
import { templateDefault } from '../../constants/emailTemplate';
import './EditEmailTemplate.scss';
import useCreateSearch from '../../hooks/useCreateSearch';
import ModalCustom from '../ModalCustom/ModalCustom';
import { TrackEventGTM } from '../../utils/filters/analyticsUtils';
import { Window } from '../../utils/window';
import { getLocalStorageWorkspace } from '../../utils/workspaceUtils';
import SafeContainerTinyMCE from '../SafeContainer/SafeContainerTinyMCE';
import EmailTagsInput from './EmailTagsInput';
import useTranslations from '../../hooks/useTranslations';
import { useLanguageTranslations } from '../../hooks/useLanguageTranslations';
import CountryFlag from '../CountryFlag/CountryFlag';
import { styleEmailPreviewListItems } from '../../utils/emailsUtils';
import { EventsGTM } from '../../constants/events';
import { translateTemplateHTML, translateTemplateLabel } from '../../utils/emailsUtils';


const EditEmailTemplate = ({
  goBack,
  handleSendRequest,
  viewInfo = true,
  emailDashboard = false,
  emails = [],
}) => {
  const { currentWorkspace } = useSelector((state) => state.auth);

  const { template, newSearch, subjectEmail } = useSelector((state) => state.screenings);
  const { step } = useCreateSearch();
  const dispatch = useDispatch();
  const { key } = useTranslations();
  const { languagesList } = useLanguageTranslations();
  const history = useHistory();
  const [value, setValue] = useState('');
  const [customEmailSelected, setCustomEmailSelected] = useState(null);
  const [selectLanguages, setSelectLanguages] = useState(
    Object.keys(customEmailSelected?.translations ?? [])
  );
  const [selectedLang, setSelectedLang] = useState(key);

  const [subject, setSubject] = useState(subjectEmail.subjectEmail);
  const regex = new RegExp(/\{(.+?)\}/g);
  const [result, setResult] = useState([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [customsEmails, setCustomsEmails] = useState([]);
  const [emailsToSend, setEmailsToSend] = useState(emails);
  const [ccEmailsToSend, setCcEmailsToSend] = useState([]);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  useEffect(() => {
    setSubject(subjectEmail.subjectEmail);
  }, [subjectEmail]);

  const mapObj = {
    '{{firstName}}': t('EMAIL_TO_SEND_FIRSTNAME'),
    '{{lastName}}': t('EMAIL_TO_SEND_LASTNAME'),
    '{{companyName}}': t('ALKEMY'),
    '{{score}}': t('EMAIL_TO_SEND_SCORE'),
    '{{email}}': 'alkemy@test.com',
  };
  useEffect(() => {
    const url = history.location.pathname;
    if (Window() && url !== '/create-template') {
      window.onpopstate = () => {
        history.goForward();
      };
    }
    return () => {
      if (Window()) {
        window.onpopstate = () => {};
      }
    };
  }, []);

  const { Option } = Select;

  useEffect(() => {
    const loadInitialData = async () => {
      if (emailDashboard) {
        const response = await getCustomEmailsAll({ workspaceId: currentWorkspace, limit: 10000 });
        setCustomsEmails(response?.collection ?? []);
        return;
      }

      if (template?.html?.length) {
        setValue(template);
        const response = await getCustomEmailsAll({ workspaceId: currentWorkspace, limit: 10000 });
        setCustomsEmails(response?.collection ?? []);
        return;
      }

      const recruitSessionStageId = newSearch?.stages[0]?.id;

      const [lastEmailResult, customEmailsResult, defaultTemplateResult] = await Promise.allSettled(
        [
          getLastEmail(recruitSessionStageId),
          getCustomEmailsAll({ workspaceId: currentWorkspace, limit: 10000 }),
          getTemplateByDefault(),
        ]
      );

      const lastEmail = lastEmailResult.status === 'fulfilled' ? lastEmailResult.value : null;
      const customEmailsList =
        customEmailsResult.status === 'fulfilled' ? customEmailsResult.value?.collection ?? [] : [];
      const defaultTemplate =
        defaultTemplateResult.status === 'fulfilled' ? defaultTemplateResult.value : null;

      let selectedTemplateToUse = null;
      let finalEmailsList = [...customEmailsList];

      if (lastEmail && Object.keys(lastEmail).length && lastEmail?.html?.length) {
        const html = lastEmail.html?.replace(
          '{{HTML}}',
          lastEmail?.translations ? lastEmail?.translations[key]?.HTML : ''
        );
        const subject = lastEmail.subject?.replace(
          'SUBJECT',
          lastEmail?.translations ? lastEmail?.translations[key]?.SUBJECT : ''
        );

        setValue(html);
        dispatch(setSubjectEmail({ subjectEmail: subject ?? '' }));
        dispatch(setTemplate({ template: html, id: lastEmail.id }));

        const existsInList = customEmailsList.find((email) => email.id === lastEmail.id);

        if (!existsInList) {
          const lastEmailWithTitle = {
            ...lastEmail,
            title: lastEmail.title || t('LAST_USED_TEMPLATE'),
          };
          finalEmailsList = [lastEmailWithTitle, ...customEmailsList];
          selectedTemplateToUse = lastEmailWithTitle;
        } else {
          selectedTemplateToUse = existsInList;
        }
      } else if (defaultTemplate && defaultTemplate?.html) {
        const html = translateTemplateHTML(defaultTemplate.html, key, defaultTemplate);
        const subject = translateTemplateLabel(defaultTemplate.subject, key, defaultTemplate);

        setValue(html);
        dispatch(setSubjectEmail({ subjectEmail: subject || '' }));
        dispatch(setTemplate({ template: html, id: defaultTemplate.id }));

        const existsInList = customEmailsList.find((email) => email.id === defaultTemplate.id);

        if (!existsInList) {
          const defaultWithTitle = {
            ...defaultTemplate,
            title: defaultTemplate.title || t('DEFAULT_TEMPLATE'),
          };
          finalEmailsList = [defaultWithTitle, ...customEmailsList];
          selectedTemplateToUse = defaultWithTitle;
        } else {
          selectedTemplateToUse = existsInList;
        }
      } else {
        const localTemplate = templateDefault(getLocalStorageWorkspace()?.companyName);
        setValue(localTemplate);
        dispatch(setTemplate({ template: localTemplate }));
        dispatch(setSubjectEmail({ subjectEmail: '' }));
      }

      setCustomsEmails(finalEmailsList);

      if (selectedTemplateToUse) {
        setCustomEmailSelected(selectedTemplateToUse);
        setSelectLanguages(Object.keys(selectedTemplateToUse?.translations || {}));
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    if (customsEmails?.length && template?.id) {
      const selectedTemplate = customsEmails.find((email) => email.id === template.id);
      if (selectedTemplate) {
        setCustomEmailSelected(selectedTemplate);
        setSelectLanguages(Object.keys(selectedTemplate?.translations || {}));
      }
    }
  }, [customsEmails, template?.id]);

  useEffect(() => {
    const loadMembers = async () => {
      setLoadingMembers(true);
      try {
        const uniqueEmails = await fetchUniqueWorkspaceMembers(currentWorkspace);
        setWorkspaceMembers(uniqueEmails);
      } catch (error) {
        console.error('Error loading workspace members:', error);
        setWorkspaceMembers([]);
      } finally {
        setLoadingMembers(false);
      }
    };
    if (currentWorkspace) {
      loadMembers();
    }
  }, [currentWorkspace]);

  useEffect(() => {
    if (
      value.match(regex) !== null &&
      !!value.match(regex).length &&
      JSON.stringify(value.match(regex)) !== JSON.stringify(result)
    )
      setResult(value.match(regex));
  }, [value]);

  const handleCustomEmail = async () => {
    TrackEventGTM(EventsGTM.EDIT_EMAIL_TEMPLATE, {
      event_category: 'Edit email',
      event_action: 'click',
    });
    if (subject !== '') {
      const recruitSessionStageId = newSearch?.stages[0]?.id;
      const res = await saveTemplateEmail(
        value,
        subject,
        recruitSessionStageId,
        t('GO_TO_PLATFORM'),
        null,
        currentWorkspace
      );
      if (res) {
        dispatch(setTemplate({ template: res.html, id: res.id }));
      }
      dispatch(setSubjectEmail({ subjectEmail: subject }));

      setShowModal(true);
    } else {
      notification.open({
        message: t('SUBJECT_EMAIL_ERROR'),
        icon: <i className="fas fa-times" style={{ color: 'red' }} />,
      });
    }
  };

  const handleGenericEmail = () => {
    TrackEventGTM(EventsGTM.EDIT_EMAIL_TEMPLATE, {
      event_category: 'Edit email',
      event_action: 'click',
    });
    dispatch(setTemplate({ template: value }));
    if (subject !== '') {
      dispatch(setSubjectEmail({ subjectEmail: subject }));
      setShowModal(true);
    } else {
      notification.open({
        message: t('SUBJECT_EMAIL_ERROR'),
        icon: <i className="fas fa-times" style={{ color: 'red' }} />,
      });
    }
  };

  const handleSendEmail = async () => {
    TrackEventGTM(EventsGTM.EDIT_EMAIL_TEMPLATE, {
      event_category: 'Edit email',
      event_action: 'click',
    });
    setShowModal(false);
    handleSendRequest(error, setLoading, emailsToSend, ccEmailsToSend);
  };
  const onChangeTemplate = (value, option) => {
    const selectedTemplate = customsEmails.find((email) => email.id === value);
    if (!selectedTemplate) return;

    setCustomEmailSelected(selectedTemplate);
    setSelectLanguages(Object.keys(selectedTemplate?.translations || {}));

    const html = translateTemplateHTML(
      selectedTemplate?.html,
      selectedLang ?? key,
      selectedTemplate
    );
    const subject = translateTemplateLabel(
      selectedTemplate?.subject,
      selectedLang ?? key,
      selectedTemplate
    );

    setValue(html);
    setSubject(subject);

    dispatch(setTemplate({ template: html, id: selectedTemplate.id }));
  };

  const onSelectLenguage = (e) => {
    const lang = e.target.value;
    setSelectedLang(lang);

    const html = translateTemplateHTML(customEmailSelected?.html, lang, customEmailSelected);
    const newsubject = translateTemplateLabel(
      customEmailSelected?.subject,
      lang,
      customEmailSelected
    );

    setValue(html);
    setSubject(newsubject);
  };

  return (
    <div data-testid="edit-email-template-component" className="w-100 mx-auto h-100">
      <ModalCustom
        title={
          emailDashboard
            ? t('CREATE_SEARCH_CLICK_SEND_SEARCH')
            : t('CREATE_SEARCH_CLICK_SEND_SCREENING')
        }
        subtitle={t('CREATE_SEARCH_CHANGE_DATA_QUESTION')}
        show={showModal}
        setShow={setShowModal}
        onOk={handleSendEmail}
        onOkDisabled={loading}
        onCancel={() => setShowModal(false)}
        classModal="modal-wrap-add-row"
        onCancelText={t('CREATE_SEARCH_CANCEL_SEARCH')}
        onOkText={emailDashboard ? t('SEND_CUSTOM_EMAIL_BTN') : t('SEND_SCREENING_EMAIL_BTN')}
        showCross={false}
        datatestidbuttonok="send-email-button"
      />
      <BackgroundTop
        title={`${t('CREATE_SEARCH_CAPSULE_TITLE')}${newSearch?.name && `: ${newSearch?.name}`}`}
        className="position-relative"
        titleClassname="text-title"
      />
      <div className="px-2">
        <div className="position-relative d-flex flex-column description-container p-container-email pt-4 bg-white rounded mx-auto justify-content-around px-4">
          {viewInfo && (
            <div className="d-flex justify-content-between align-items-start">
              {step <= 3 && (
                <span className="text-xs  text-muted">
                  {t('CREATE_SEARCH_PLACED_STEP').replace('{step}', step)}
                </span>
              )}
            </div>
          )}
          <h3 className=" font-weight-600 text-base my-3">{t('EMAIL_DASHBOARD_TITLE')}</h3>
          <h3 className=" font-weight-500 text-xs mt-2 gray-subtitle">
            {t('EMAIL_DASHBOARD_SUBTITLE')}
          </h3>
          <div className="d-flex flex-column mt-3">
            <div className="w-100 d-flex flex-column justify-content-between">
              {emails?.length ? (
                <>
                  <Row gutter={24} className="mb-2">
                    <Col xs={24} md={12}>
                      <Typography.Text strong className="d-block mb-2">
                        {t('TO')}:
                      </Typography.Text>
                    </Col>
                    <Col xs={24} md={12}>
                      <Typography.Text strong className="d-block mb-2">
                        {t('CC_LABEL')}:
                      </Typography.Text>
                      <Typography.Text type="secondary" className="d-block text-sm">
                        {t('CC_DESCRIPTION')}
                      </Typography.Text>
                    </Col>
                  </Row>
                  <Row gutter={24} className="mb-3">
                    <Col xs={24} md={12}>
                      <EmailTagsInput setEmails={setEmailsToSend} emails={emailsToSend} />
                    </Col>
                    <Col xs={24} md={12}>
                      <Spin spinning={loadingMembers} size="small">
                        <EmailTagsInput
                          setEmails={setCcEmailsToSend}
                          emails={ccEmailsToSend}
                          suggestions={workspaceMembers}
                          placeholder={t('CC_PLACEHOLDER')}
                        />
                      </Spin>
                    </Col>
                  </Row>
                </>
              ) : (
                <div className="mb-3">
                  <Typography.Text strong className="d-block mb-2">
                    {t('CC_LABEL')}:
                  </Typography.Text>
                  <Typography.Text type="secondary" className="d-block text-sm mb-2">
                    {t('CC_DESCRIPTION')}
                  </Typography.Text>
                  <Spin spinning={loadingMembers} size="small">
                    <EmailTagsInput
                      setEmails={setCcEmailsToSend}
                      emails={ccEmailsToSend}
                      suggestions={workspaceMembers}
                      placeholder={t('CC_PLACEHOLDER')}
                    />
                  </Spin>
                </div>
              )}

              <div className="mb-3">
                <Select
                  placeholder={t('TEMPLATE')}
                  value={
                    customsEmails?.length > 0 &&
                    customsEmails?.find((email) => email.id === template?.id)
                      ? template?.id
                      : undefined
                  }
                  onChange={onChangeTemplate}
                  className="w-100"
                  loading={!customsEmails?.length}
                  options={customsEmails
                    ?.filter((item) => item?.sendType === 'MANUAL')
                    ?.map((item) => ({
                      ...item,
                      value: item?.id,
                      label: translateTemplateLabel(item?.title, key, item) || item?.title,
                    }))}
                  dropdownMatchSelectWidth={false}
                />
              </div>

              {customEmailSelected && (
                <div className="mb-3">
                  <Radio.Group value={selectedLang ?? key} onChange={onSelectLenguage}>
                    {selectLanguages?.map((lang) => (
                      <Radio.Button value={lang}>
                        <CountryFlag
                          countryIso={languagesList?.find((item) => item?.value === lang)?.iso}
                          size="sm"
                        />
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </div>
              )}

              <div className="mb-3">
                <Input
                  data-testid="subject-input"
                  value={subject}
                  placeholder={t('EMAIL_TEMPLATE_PLACEHOLDER_SUBJECT')}
                  size="large"
                  onChange={(e) => {
                    setSubject(e.target.value);
                  }}
                />
              </div>
              <Card className="mb-3 variables-info-card">
                <Typography.Title level={5}>{t('AVAILABLE_VARIABLES')}</Typography.Title>
                <Space wrap className="mb-2">
                  <Tooltip title={t('VARIABLE_FIRSTNAME_DESC')}>
                    <Tag color="blue" className="cursor-pointer">
                      {'{{firstName}}'}
                    </Tag>
                  </Tooltip>
                  <Tooltip title={t('VARIABLE_LASTNAME_DESC')}>
                    <Tag color="blue" className="cursor-pointer">
                      {'{{lastName}}'}
                    </Tag>
                  </Tooltip>
                  <Tooltip title={t('VARIABLE_COMPANY_DESC')}>
                    <Tag color="blue" className="cursor-pointer">
                      {'{{companyName}}'}
                    </Tag>
                  </Tooltip>
                  <Tooltip title={t('VARIABLE_EMAIL_DESC')}>
                    <Tag color="blue" className="cursor-pointer">
                      {'{{email}}'}
                    </Tag>
                  </Tooltip>
                  {emailDashboard && (
                    <Tooltip title={t('VARIABLE_SCORE_DESC')}>
                      <Tag color="blue" className="cursor-pointer">
                        {'{{score}}'}
                      </Tag>
                    </Tooltip>
                  )}
                </Space>
                <Typography.Text type="secondary" className="text-sm">
                  {t('TEXT_CREATE_TEMPLATE')}
                </Typography.Text>
              </Card>
              <Alert
                message={t('LOGO_WILL_BE_ADDED_AUTOMATICALLY')}
                type="info"
                icon={<InfoCircleOutlined />}
                className="mb-3"
                showIcon
              />
              {typeof window !== 'undefined' && (
                <div className="mb-3">
                  <SafeContainerTinyMCE
                    content={value}
                    handleChange={(val) => {
                      setValue(val);
                    }}
                    placeholder={t('EMAIL_TEMPLATE_PLACEHOLDER_CONTENT')}
                    height={400}
                    language={selectedLang || key}
                  />
                </div>
              )}
              {error && (
                <Alert message={t('ATTRIBUTE_IS_INVALID')} type="error" className="mt-3" showIcon />
              )}
            </div>
          </div>
          <div className="d-flex flex-row">
            <button
              data-testid="go-back-button"
              className="button-screening-continue px-4 py-1 rounded-0 my-3 mr-auto"
              type="button"
              onClick={() => {
                if (goBack) {
                  goBack();
                } else {
                  history.goBack();
                }
              }}
            >
              <span className="button-screening-continue-text  text-xs font-weight-bold">
                {t('CREATE_SEARCH_STAGE_BACK')}
              </span>
            </button>
            <button
              disabled={loading}
              data-testid="send-evaluation-button"
              className="button-screening-continue ml-auto px-4 py-1 rounded-0 my-3"
              type="button"
              onClick={emailDashboard ? handleGenericEmail : handleCustomEmail}
            >
              <span className="button-screening-continue-text  text-xs font-weight-bold">
                {t('SCREENING_SEND')}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditEmailTemplate;
